import {type LngLat, type MapLocationRequest, type Margin, MapListener} from '@yandex-delivery/blocks'
import type {Optional} from '@yandex-delivery/types'
import {isPoint} from '@yandex-taxi/react-integration-api'
import React, {useCallback, useMemo} from 'react'

import {useRouteOptimizationButton} from '../../../widgets/Address/components/AddressWidget/hooks/use-route-optimization-button'
import {PointPlacemark} from '../PointPlacemark/PointPlacemark'
import {useMapZoomCenterUpdate} from './hooks'
import {getPlacemarkType} from './utils'

type Props = {
  allPoints: Optional<LngLat>[]
  route: LngLat[]
  onChange: (newCoords: LngLat, index: number) => void
  isDragPointAllowed?: boolean
  onLocationRequest?: (location: MapLocationRequest) => void
  margin?: Margin
}

export function MapPlacemarks(props: Props): React.ReactElement {
  const {route, allPoints, isDragPointAllowed = false, onChange, onLocationRequest, margin} = props
  const [sourcePoint, ...destinationPoints] = allPoints
  const destinationPointsCount = destinationPoints.length
  const validDestinationPointsCount = destinationPoints.filter(isPoint).length
  const {setRouteUnoptimized} = useRouteOptimizationButton()

  const preparedPoints = useMemo(() => {
    return allPoints.filter(isPoint)
  }, [allPoints])

  const handleMapClick = useCallback(
    (coords: LngLat) => {
      if (sourcePoint === undefined) {
        onChange(coords, 0)
        return
      }

      let lastIndex = route.length

      if (route.length === destinationPointsCount) {
        lastIndex -= 1
      }

      onChange(coords, lastIndex)
      setRouteUnoptimized()
    },
    [sourcePoint, route.length, destinationPointsCount, onChange, setRouteUnoptimized],
  )

  useMapZoomCenterUpdate(route, onLocationRequest, margin)

  return (
    <>
      {preparedPoints.map((point, index) => {
        return (
          <PointPlacemark
            key={index}
            coordinates={point}
            draggable={isDragPointAllowed}
            onDragEnd={coordinates => {
              onChange(coordinates, index)
              setRouteUnoptimized()
            }}
            type={getPlacemarkType(index, validDestinationPointsCount)}
          >
            {index + 1}
          </PointPlacemark>
        )
      })}
      <MapListener
        onClick={(_, e) => {
          handleMapClick(e.coordinates)
        }}
      />
    </>
  )
}
